// Les chiffres du tableau de bord — le raisonnement, pur et testé à part.
//
// Ce module ne lit rien : on lui donne les lignes déjà lues (factures, devis,
// statuts de chantiers, événements d'agenda) et il rend les séries prêtes à
// afficher. La page web ne fait que les aller chercher et les dessiner.
//
// Deux règles d'argent, les mêmes que dans `suivi-facturation`, valent partout
// ici :
//   - une facture `brouillon` ou `annulee_avoir` n'est pas du chiffre d'affaires ;
//   - un avoir vient en DÉDUCTION, sauf quand il annule une facture déjà sortie
//     du calcul — la retrancher une seconde fois compterait l'annulation deux fois.

/** Les mois portent leur abréviation française : l'axe est lu de loin. */
const MOIS_COURTS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** Les statuts qui ne sont PAS du chiffre d'affaires. */
const STATUTS_NON_EMIS = new Set(['brouillon', 'annulee_avoir']);

/** Un devis à ces statuts-là engage le client : il a dit oui. */
const STATUTS_DEVIS_SIGNE = new Set(['signe', 'converti_facture']);

/** Un devis parti chez le client, quoi qu'il soit devenu depuis. */
const STATUTS_DEVIS_ENVOYE = new Set([
  'envoye',
  'consulte',
  'signe',
  'refuse',
  'expire',
  'converti_facture',
]);

/** Un devis à ces statuts-là attend encore une réponse du client. */
const STATUTS_DEVIS_ATTENTE = new Set(['envoye', 'consulte']);

/** Les chantiers qui occupent des équipes aujourd'hui. */
const STATUTS_CHANTIER_ACTIF = new Set(['planifie', 'en_cours', 'pause']);

/** Le nombre de mois de l'histogramme. */
const MOIS_HISTOGRAMME = 12;

/** Sur combien de mois se juge la transformation des devis. */
const MOIS_ENTONNOIR = 3;

/** En deçà, un reste à payer n'est plus qu'une poussière d'arrondi. */
const CENTIME = 0.005;

export interface FactureTableauBord {
  id: string;
  numero: string;
  /** acompte | situation | finale | avoir | standard */
  kind: string;
  statut: string;
  objet: string | null;
  /** Jour d'émission, `AAAA-MM-JJ`. */
  dateEmission: string;
  /** Jour d'échéance, `AAAA-MM-JJ`. Null quand elle n'est pas renseignée. */
  dateEcheance: string | null;
  montantHt: number;
  montantTtc: number;
  montantPaye: number;
  /** Pour un avoir : la facture qu'il annule. */
  factureLieeId: string | null;
}

export interface DevisTableauBord {
  statut: string;
  /** Jour d'émission, `AAAA-MM-JJ`. */
  dateEmission: string;
  montantHt: number;
}

export interface EvenementTableauBord {
  id: string;
  titre: string;
  /** Début de l'intervention, horodatage ISO. */
  debutAt: string;
  journeeEntiere: boolean;
  chantierId: string | null;
}

export interface EntreeTableauBord {
  factures: FactureTableauBord[];
  devis: DevisTableauBord[];
  /** Le statut de chaque chantier vivant — seul leur nombre nous intéresse. */
  statutsChantiers: string[];
  /** Les événements d'agenda des sept prochains jours, déjà bornés à la lecture. */
  evenements: EvenementTableauBord[];
  /** Le jour où l'on se place, `AAAA-MM-JJ`. */
  jour: string;
}

/** Un mois de l'histogramme : ce qui a été facturé, ce qui a été signé. */
export interface PointMois {
  /** `AAAA-MM`, pour trier et comparer. */
  cle: string;
  label: string;
  factureHt: number;
  signeHt: number;
}

/** Une tranche d'ancienneté d'impayé. */
export interface TrancheImpaye {
  label: string;
  montant: number;
  nombre: number;
}

/** Une étape de l'entonnoir devis, sur la période retenue. */
export interface EtapeEntonnoir {
  label: string;
  nombre: number;
  montantHt: number;
}

/** Une échéance des sept prochains jours : intervention ou facture. */
export interface Echeance {
  id: string;
  /** Le jour où elle tombe, `AAAA-MM-JJ`. */
  date: string;
  titre: string;
  lien: string;
  nature: 'agenda' | 'facture';
  /**
   * Pour une intervention placée à une heure : son début (ISO). Null pour une
   * journée entière comme pour une facture. La page en fait une heure lisible :
   * la mise en forme regarde la langue de l'écran, pas ce calcul.
   */
  debutAt: string | null;
  /** Pour une facture : son objet. Null pour une intervention. */
  objet: string | null;
  /** Pour une facture : ce qu'il reste à encaisser. Null pour une intervention. */
  montant: number | null;
}

export interface ChiffresTableauBord {
  /** CA facturé HT du mois en cours, et du précédent pour la comparaison. */
  caMoisHt: number;
  caMoisPrecedentHt: number;
  /** Ce qui reste dû sur les factures émises, et la part échue depuis + de 30 j. */
  encours: number;
  encoursRetardLong: number;
  /** Devis envoyés qui attendent encore une réponse. */
  devisEnAttente: number;
  devisEnAttenteMontantHt: number;
  /** Part des devis envoyés qui a été signée sur 3 mois. Null si aucun envoi. */
  tauxTransformation: number | null;
  chantiersActifs: number;
  mois: PointMois[];
  impayes: TrancheImpaye[];
  entonnoir: EtapeEntonnoir[];
  echeances: Echeance[];
}

/** La clé du mois d'un jour : `2026-09-18` → `2026-09`. */
function cleMois(jour: string): string {
  return jour.slice(0, 7);
}

function libelleMois(cle: string): string {
  return MOIS_COURTS[Number(cle.slice(5, 7)) - 1] ?? cle;
}

/** Les `n` derniers mois jusqu'à celui de `jour`, du plus ancien au plus récent. */
function derniersMois(jour: string, n: number): string[] {
  const annee = Number(jour.slice(0, 4));
  const mois = Number(jour.slice(5, 7));
  const cles: string[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    // Le 1er du mois : la date ne déborde jamais sur le mois suivant, et le
    // décalage d'année se fait tout seul.
    const d = new Date(Date.UTC(annee, mois - 1 - i, 1));
    cles.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return cles;
}

/** Le jour `n` jours après celui-ci, `AAAA-MM-JJ`. */
export function jourPlus(jour: string, n: number): string {
  const d = new Date(`${jour}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Le nombre de jours entre deux jours du calendrier (positif si `a` est passé). */
function joursEcoules(a: string, jour: string): number {
  return Math.round((Date.parse(`${jour}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
}

export function calculerTableauBord(entree: EntreeTableauBord): ChiffresTableauBord {
  const { factures, devis, statutsChantiers, evenements, jour } = entree;

  // Émettre un avoir fait passer la facture d'origine en `annulee_avoir` : les
  // deux s'effacent ensemble. Un avoir dont la facture est déjà sortie du
  // calcul n'a donc plus rien à retrancher.
  const comptees = new Set(
    factures.filter((f) => !STATUTS_NON_EMIS.has(f.statut)).map((f) => f.id),
  );
  const emises = factures.filter(
    (f) =>
      !STATUTS_NON_EMIS.has(f.statut) &&
      (f.kind !== 'avoir' || f.factureLieeId === null || comptees.has(f.factureLieeId)),
  );
  const signe = (f: FactureTableauBord): number => (f.kind === 'avoir' ? -1 : 1);

  // ---- Les douze mois : facturé HT contre devis signé HT.
  const clesMois = derniersMois(jour, MOIS_HISTOGRAMME);
  const parMois = new Map<string, PointMois>(
    clesMois.map((cle) => [cle, { cle, label: libelleMois(cle), factureHt: 0, signeHt: 0 }]),
  );
  for (const f of emises) {
    const point = parMois.get(cleMois(f.dateEmission));
    if (point) point.factureHt += signe(f) * f.montantHt;
  }
  for (const d of devis) {
    if (!STATUTS_DEVIS_SIGNE.has(d.statut)) continue;
    const point = parMois.get(cleMois(d.dateEmission));
    if (point) point.signeHt += d.montantHt;
  }
  // La Map a été remplie dans l'ordre des mois : ses valeurs le sont aussi.
  const mois = [...parMois.values()];

  // ---- Les impayés, par ancienneté de l'échéance.
  const aEchoir: TrancheImpaye = { label: 'À échoir', montant: 0, nombre: 0 };
  const moins30: TrancheImpaye = { label: '0-30 j', montant: 0, nombre: 0 };
  const moins60: TrancheImpaye = { label: '30-60 j', montant: 0, nombre: 0 };
  const plus60: TrancheImpaye = { label: '+ de 60 j', montant: 0, nombre: 0 };
  let encours = 0;
  let encoursRetardLong = 0;
  for (const f of emises) {
    if (f.kind === 'avoir') continue;
    const reste = f.montantTtc - f.montantPaye;
    if (reste <= CENTIME) continue;
    encours += reste;
    // Sans échéance connue, on ne peut pas dire qu'elle est dépassée : la
    // créance est comptée comme à échoir plutôt qu'accusée d'un retard.
    const retard = f.dateEcheance === null ? 0 : joursEcoules(f.dateEcheance, jour);
    const tranche =
      retard <= 0 ? aEchoir : retard <= 30 ? moins30 : retard <= 60 ? moins60 : plus60;
    tranche.montant += reste;
    tranche.nombre += 1;
    // Au-delà de 30 jours de retard, une créance ne rentre plus toute seule.
    if (tranche === moins60 || tranche === plus60) encoursRetardLong += reste;
  }

  // ---- L'entonnoir devis, sur les trois derniers mois d'émission.
  const debutEntonnoir = clesMois[clesMois.length - MOIS_ENTONNOIR] ?? clesMois[0] ?? '';
  const recents = devis.filter((d) => cleMois(d.dateEmission) >= debutEntonnoir);
  const envoyes = recents.filter((d) => STATUTS_DEVIS_ENVOYE.has(d.statut));
  const signes = recents.filter((d) => STATUTS_DEVIS_SIGNE.has(d.statut));
  const factures3Mois = recents.filter((d) => d.statut === 'converti_facture');
  const totalHt = (liste: DevisTableauBord[]): number => liste.reduce((s, d) => s + d.montantHt, 0);
  const enAttente = devis.filter((d) => STATUTS_DEVIS_ATTENTE.has(d.statut));

  // ---- Les sept prochains jours : interventions et factures qui tombent.
  const finSemaine = jourPlus(jour, 7);
  const echeances: Echeance[] = [
    ...evenements.map((e) => ({
      id: `ev-${e.id}`,
      date: e.debutAt.slice(0, 10),
      titre: e.titre,
      lien: e.chantierId === null ? '/agenda' : `/chantiers/${e.chantierId}`,
      nature: 'agenda' as const,
      debutAt: e.journeeEntiere ? null : e.debutAt,
      objet: null,
      montant: null,
    })),
    ...emises.flatMap((f) => {
      const echeance = f.dateEcheance;
      const reste = f.montantTtc - f.montantPaye;
      if (f.kind === 'avoir' || echeance === null) return [];
      if (echeance < jour || echeance > finSemaine || reste <= CENTIME) return [];
      return [
        {
          id: `fa-${f.id}`,
          date: echeance,
          titre: `Échéance ${f.numero}`,
          lien: `/factures/${f.id}`,
          nature: 'facture' as const,
          debutAt: null,
          objet: f.objet,
          montant: reste,
        },
      ];
    }),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return {
    caMoisHt: mois[mois.length - 1]?.factureHt ?? 0,
    caMoisPrecedentHt: mois[mois.length - 2]?.factureHt ?? 0,
    encours,
    encoursRetardLong,
    devisEnAttente: enAttente.length,
    devisEnAttenteMontantHt: totalHt(enAttente),
    tauxTransformation: envoyes.length === 0 ? null : signes.length / envoyes.length,
    chantiersActifs: statutsChantiers.filter((s) => STATUTS_CHANTIER_ACTIF.has(s)).length,
    mois,
    impayes: [aEchoir, moins30, moins60, plus60],
    entonnoir: [
      { label: 'Envoyés', nombre: envoyes.length, montantHt: totalHt(envoyes) },
      { label: 'Signés', nombre: signes.length, montantHt: totalHt(signes) },
      { label: 'Facturés', nombre: factures3Mois.length, montantHt: totalHt(factures3Mois) },
    ],
    echeances,
  };
}
